import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdDeleteOutline, MdEdit, MdAddCircle, MdSearch, MdRefresh, MdFileDownload } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiCopy, FiExternalLink, FiLink, FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  handleSessionExpired,
  getDefaultPageSize,
  ONELINK_LINK_STATUS,
  ONELINK_MEDIA_SOURCE,
  getLinkStatusDisplay,
  getCampaignTypeLabel,
  getMediaSourceLabel,
  buildCampaignDetailNavigation,
  fetchApplicationAppId,
  can,
  exportListCsv,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import OneLinkCreateModal from "./OneLinkCreateModal";
import { campaignToDraft } from "./oneLinkUtils";

const EMPTY_FILTERS = { name: "", campaign_code: "", channel: "", status: "" };

export default function OneLinkList() {
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams();
  const [appUuid, setAppUuid] = useState(null);
  const loadApp = useCallback(async () => {
    const uuid = await fetchApplicationAppId(routeAppId);
    if (uuid) setAppUuid(uuid);
    return uuid;
  }, [routeAppId]);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);

  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: () => 2 + (can("onelink.edit") ? 1 : 0) + (can("onelink.delete") ? 1 : 0),
    menuWidth: 220,
    useAnchored: true,
  });

  const openCampaignDetails = useCallback(
    (row) => {
      if (!routeAppId || !row?.id) return;
      const { path, state } = buildCampaignDetailNavigation(routeAppId, row.id, {
        backSource: "onelink",
        listSearch: searchParams.toString(),
      });
      navigate(path, { state });
    },
    [navigate, routeAppId, searchParams]
  );

  const channelOptions = useMemo(
    () => [{ value: "", label: "All media sources" }, ...ONELINK_MEDIA_SOURCE.map((m) => ({ value: String(m.value), label: m.label }))],
    []
  );

  const statusOptions = useMemo(
    () => [{ value: "", label: "All status" }, ...ONELINK_LINK_STATUS.map((s) => ({ value: String(s.value), label: s.label }))],
    []
  );

  const loadData = useCallback(
    async (nextPage = page, nextFilters = filters, uuid = appUuid) => {
      if (!uuid) return;
      setLoading(true);
      const res = await fetchWithAuth(`${Utils.baseurl}get/onelinks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_id: uuid,
          page: nextPage,
          pageSize,
          name: (nextFilters.name || "").trim(),
          campaign_code: (nextFilters.campaign_code || "").trim(),
          channel: nextFilters.channel,
          status: nextFilters.status,
        }),
      });
      const result = await res.json().catch(() => ({}));
      setLoading(false);
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Failed to load links");
        return;
      }
      setRows(result.data || []);
      setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
    },
    [appUuid, page, filters, pageSize]
  );

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if ((f.campaign_code || "").trim()) sp.set("campaign_code", f.campaign_code.trim());
    if (String(f.channel || "").trim()) sp.set("channel", String(f.channel).trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    if (!routeAppId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const f = {
      name: searchParams.get("name") || "",
      campaign_code: searchParams.get("campaign_code") || "",
      channel: searchParams.get("channel") || "",
      status: searchParams.get("status") || "",
    };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(f.name || f.campaign_code || f.channel || f.status));
    (async () => {
      const uuid = (await loadApp()) || appUuid;
      if (uuid) loadData(p, f, uuid);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAppId, searchParams]);

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean(f.name || f.campaign_code || f.channel || f.status));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, EMPTY_FILTERS);
  };

  const getTypeLabel = (c) => {
    return getCampaignTypeLabel(c.campaign_type) || "—";
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Tracking link copied");
  };

  const openCreate = () => {
    setEditDraft(null);
    setCreateOpen(true);
  };

  const openEdit = (c) => {
    setEditDraft(campaignToDraft(c));
    setCreateOpen(true);
  };

  const closeModal = () => {
    setCreateOpen(false);
    setEditDraft(null);
  };

  const onSaved = () => {
    loadData(page, filters);
    closeModal();
  };

  const deleteOneLink = async (campaignId) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This link will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}delete/onelink`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: campaignId, app_id: appUuid }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success("Deleted");
    loadData(page, filters);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("get/onelinks", {
        app_id: appUuid,
        name: (filters.name || "").trim(),
        campaign_code: (filters.campaign_code || "").trim(),
        channel: filters.channel,
        status: filters.status,
      }, "onelink.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>OneLink</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData(page, filters)}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("onelink.add") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={openCreate}>
                <MdAddCircle />
                <span>Create link</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input
                      className="form-control"
                      placeholder="Campaign name"
                      value={draftFilters.name}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input
                      className="form-control"
                      placeholder="Campaign code"
                      value={draftFilters.campaign_code}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, campaign_code: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="onelink-filter-channel"
                      instanceId="onelink-filter-channel"
                      classNamePrefix="ns-onelink-filter-channel"
                      isSearchable
                      placeholder="All media sources"
                      options={channelOptions}
                      value={channelOptions.find((o) => o.value === String(draftFilters.channel || "")) || channelOptions[0]}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, channel: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="onelink-filter-status"
                      instanceId="onelink-filter-status"
                      classNamePrefix="ns-onelink-filter-status"
                      isSearchable
                      placeholder="All status"
                      options={statusOptions}
                      value={statusOptions.find((o) => o.value === String(draftFilters.status || "")) || statusOptions[0]}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onApplyFilters}>
                        <MdSearch />
                        <span>Search</span>
                      </button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onResetFilters}>
                        <MdRefresh />
                        <span>Reset</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Campaign name</th>
                      <th>Type</th>
                      <th>Media source</th>
                      <th>Tracking URL</th>
                      <th>Created by</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "center" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No links found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => {
                        const st = getLinkStatusDisplay(r.status);
                        return (
                          <tr
                            key={r.id}
                            className="campaign-perf-row--clickable"
                            onClick={() => openCampaignDetails(r)}
                          >
                            <td>
                              <div style={{ lineHeight: 1.3 }}>
                                <span>{r.name}</span>
                                {r.campaign_code && (
                                  <div style={{ fontSize: '0.72em', color: '#999' }}>{r.campaign_code}</div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="engage-type-pill">{getTypeLabel(r)}</span>
                            </td>
                            <td>{getMediaSourceLabel(r.channel) || "—"}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <a href={r.tracking_url} target="_blank" rel="noreferrer" className="me-2">
                                <FiLink /> {r.short_code || r.campaign_code}
                              </a>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                aria-label="Copy link"
                                onClick={() => copyLink(r.tracking_url)}
                              >
                                <FiCopy />
                              </button>
                            </td>
                            <td>{r.created_by_label || "—"}</td>
                            <td>
                              <span className={st.badgeClass}>{st.label}</span>
                            </td>
                            <td
                              className="col-action"
                              style={{ textAlign: "center" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                                <button
                                  type="button"
                                  className="dropdown-trigger-btn btn btn-link p-0"
                                  aria-label="Actions"
                                  aria-expanded={openActionId === r.id}
                                  onClick={(e) => handleOpenDropdown(e, r.id, r)}
                                >
                                  <BsThreeDotsVertical />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total}
                pageSize={pageSize}
                totalPages={meta.totalPages}
                ariaLabel="OneLink pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {openActionItem && dropdownPosition
          ? createPortal(
              <div
                ref={dropdownRef}
                style={{
                  position: "fixed",
                  left: dropdownPosition.left,
                  top: dropdownPosition.top,
                  zIndex: 999999,
                  minWidth: "220px",
                }}
              >
                <div className="dropdown-menu-subscription">
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => openCampaignDetails(openActionItem))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <FiExternalLink size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>View details</span>
                  </button>
                  {can("onelink.edit") ? (
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => openEdit(openActionItem))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Edit link</span>
                  </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleDropdownAction(() => copyLink(openActionItem.tracking_url))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <FiCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Copy link</span>
                  </button>
                  {can("onelink.delete") ? (
                  <button
                    type="button"
                    className="delete-class"
                    onClick={() => handleDropdownAction(() => deleteOneLink(openActionItem.id))}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "0.875rem" }}>Delete link</span>
                  </button>
                  ) : null}
                </div>
              </div>,
              document.body
            )
          : null}

        <OneLinkCreateModal
          show={createOpen}
          onHide={closeModal}
          appUuid={appUuid}
          editingDraft={editDraft}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}
